import { type FormEvent, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ErrorAlert } from '../components/ErrorAlert.tsx';
import { Button, Card, CardBody, CardHeader, Input } from '../components/ui/index.ts';
import { useAuth } from '../hooks/useAuth.tsx';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { session?: string; email?: string } | undefined;
  const { login } = useAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Si no viene de un challenge → bounce a /login
  if (!state?.session || !state?.email) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!state?.session || !state?.email) {
      setError('Sesión inválida');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 12) {
      setError('Mínimo 12 caracteres');
      return;
    }
    // Cognito policy: lowercase + uppercase + digit + symbol
    const hasLower = /[a-z]/.test(newPassword);
    const hasUpper = /[A-Z]/.test(newPassword);
    const hasDigit = /\d/.test(newPassword);
    const hasSymbol = /[^a-zA-Z\d]/.test(newPassword);
    if (!hasLower || !hasUpper || !hasDigit || !hasSymbol) {
      setError('Debe incluir mayúscula, minúscula, número y símbolo');
      return;
    }

    setLoading(true);
    try {
      const cognitoClientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
      const cognitoRegion =
        (import.meta.env.VITE_COGNITO_REGION as string | undefined) ?? 'us-east-1';

      const res = await fetch(`https://cognito-idp.${cognitoRegion}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
        },
        body: JSON.stringify({
          ChallengeName: 'NEW_PASSWORD_REQUIRED',
          ClientId: cognitoClientId,
          Session: state.session,
          ChallengeResponses: {
            USERNAME: state.email,
            NEW_PASSWORD: newPassword,
          },
        }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({ message: 'Error' }));
        throw new Error((errBody as { message?: string }).message ?? 'Error al cambiar contraseña');
      }

      const body = (await res.json()) as { AuthenticationResult?: { IdToken?: string } };
      if (!body.AuthenticationResult?.IdToken) throw new Error('Sin IdToken en respuesta');

      login(body.AuthenticationResult.IdToken);
      navigate('/tenants', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-primary-50 p-4">
      <Card variant="elevated" className="w-full max-w-md">
        <CardHeader className="text-center">
          <h1 className="text-h3 text-content-primary mb-1">Cambiar contraseña</h1>
          <p className="text-body-sm text-content-tertiary">
            Es tu primer login. Por favor define una nueva contraseña.
          </p>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && <ErrorAlert message={error} />}
            <Input
              id="newPassword"
              name="newPassword"
              label="Nueva contraseña"
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Input
              id="confirm-password"
              name="confirmPassword"
              label="Confirmar contraseña"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full mt-4"
              loading={loading}
            >
              Cambiar contraseña y entrar
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
